import * as React from "react";
import { useSelector } from "react-redux";
import {
    booleanValueRenderer,
    customValueRenderer,
    dateFilterColumn,
    dateValueRenderer,
    dropDownColumn,
    freeTextColumn,
    multiSelectColumn,
    nonFilterableColumn,
} from "../../components/DataTable";
import { getCountryName } from "../../utils/countries";
import {
    BooleanField,
    CountryField,
    CurrencyField,
    DateField,
    DecimalField,
    ExchangeField,
    IntField,
    MultipleCountriesField,
    MultipleExchangesField,
    MultipleSelectField,
    MultipleSourcesField,
    SecurityField,
    SelectField,
    SourceField,
    TextField,
} from "../../components/Form";
import { getSecurityName } from "../../store/securities";
import { useRef, useState } from "react";
import QueryDataTable from "../../components/QueryDataTable";
import {
    AddRecordDialog,
    EditRecordDialog,
} from "../../components/RecordDialog";
import FileImporterDialog from "../../components/FileImporterDialog";
import {
    Add,
    Edit,
    FileUpload,
    LibraryAdd,
    Save,
} from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import Authentication from "../../modules/authentication";
import { API_DATE_FORMAT } from "../../hooks/useLoadAllData";

const gApiClient = Authentication.getAPIClient();

// Security asset type
export const ASSET_TYPE = {
    coco_bond: "CoCo Bond",
    commercial_paper: "Commercial Paper",
    common_stock: "Common Stock",
    convertible_bond: "Convertible Bond",
    makam: "Makam",
    mlp: "MLP",
    preferred_stock: "Preferred Stock",
    straight_bond: "Straight Bond",
    structured_bond: "Structured Bond",
    technology_closed_fund: "Technology Closed Fund",
    adr: "ADR",
    gdr: "GDR",
    unit: "Unit",
    cd: "CD",
    etfs: "ETFs",
};

// Security underlying asset
export const UNDERLYING_ASSET = {
    bonds: "Bonds",
    equity: "Equity",
    etp: "ETP",
};

// Security issue type
export const ISSUE_TYPE = {
    corporate: "Corporate",
    government: "Government",
};

// TASE Sub industry types
const TASE_SUB_INDUSTRY = {
    government: "ממשלתי",
    biotech: "ביוטכנולוגיה",
    medical_devices: "מכשור רפואי",
    pharmaceuticals: "פארמה",
    cannabis: "קנאביס",
    investments_in_life_science: "השקעות במדעי החיים",
    it_services: "שירותי מידע",
    software_and_internet: "תוכנה ואינטרנט",
    electronics_and_optics: "אלקטרוניקה ואופטיקה",
    robotics_and_3d: "רובוטיקה ותלת מימד",
    defence: "ביטחוניות",
    communications_equipment: "ציוד תקשורת",
    semiconductors: "מוליכים למחצה",
    cleantech: "קלינטק",
    renewable_energy: "אנרגיה מתחדשת",
    foodtech: "פודטק",
    tech_investments: "השקעות בהיי-טק",
    tech_funds: "קרנות היי טק",
    banks: "בנקים",
    insurance: "ביטוח",
    diversified_financials: "שירותים פיננסיים",
    non_bank_credits: "אשראי חוץ בנקאי",
    commerce: "מסחר",
    retail_stores: "רשתות שיווק",
    services: "שירותים",
    hotels_and_tourism: "מלונאות ותיירות",
    communications_and_media: "תקשורת ומדיה",
    domestic_real_estate: 'נדל"ן מניב בישראל',
    overseas_real_estate: 'נדל"ן מניב בחו"ל',
    construction: "בנייה",
    food: "מזון",
    fashion_and_clothing: "אופנה והלבשה",
    metal: "מתכת ומוצרי בניה",
    electrical: "חשמל",
    chemical_rubber_and_plastic: "כימיה, גומי ופלסטיק",
    wood_and_paper: "עץ, נייר ודפוס",
    investment_and_holdings: "השקעה ואחזקות",
    shell_companies: "חברות מעטפת",
    oil_and_gas_exploration: "חיפושי נפט וגז",
    energy: "אנרגיה",
    etfs: "קרנות סל",
    structured_bonds: 'אג"ח מובנות',
};

// TASE industry types
const TASE_INDUSTRY = {
    government: "ממשלתי",
    biomed: "ביומד",
    technology: "טכנולוגיה",
    banks: "בנקים",
    insurance: "ביטוח",
    diversified_financials: "שירותים פיננסיים",
    commerce_and_services: "מסחר ושירותים",
    real_estate_and_construction: 'נדל"ן ובנייה',
    industrials: "תעשיה",
    investment_and_holdings: "השקעה ואחזקות",
    energy_and_oil_gas_exploration: "אנרגיה וחיפושי נפט וגז",
    financial_products: "מכשירים פיננסיים",
};

// Security sector class
const SECTOR_CLASS = {
    government: "ממשלתי",
    energy: "אנרגיה",
    health_care: "בריאות",
    materials: "חומרי גלם",
    technology: "טכנולוגיה",
    real_estate: 'נדל"ן',
    financials: "פיננסים",
    consumer_staples: "צריכה בסיסית",
    consumer_discretionary: "צריכה מחזורית",
    industrials: "תעשיה",
    communication_services: "תקשורת",
    utilities: "תשתיות",
    financial_products: "מכשירים פיננסיים",
    other: "אחר",
};

// Security trading status
const TRADING_STATUS = {
    delisted: "Delisted",
    halted: "Halted",
    illiquid_list: "דלי סחירות",
    listed: "רציף",
    maintenance_list: "שימור",
};

// Security Collateral type
const COLLATERAL_TYPE = {
    first_floating: "צף ראשון",
    second_floating: "צף שני",
    first_lien: "קבוע ראשון",
    second_lien: "קבוע שני",
    third_lien: "קבוע שלישי",
};

// Security Collateral description
const COLLATERAL_DESCRIPTION = {
    listed_securities: 'ני"ע סחירים',
    unlisted_securities: "מניות של חברה פעילה",
    real_estate: 'נכסי נדל"ן',
    intangible_assets: "נכסי מטלטלין",
    government_guarantee: "ערבות מדינה",
    company_guarantee: "ערבות חברה",
    bank_account: "חשבון בנק",
    bank_deposit: "פיקדון בבנק",
    rights: "זכויות",
    excesses_project_revenue: "רווחי פרויקט",
    mixed_collaterals: "שילוב בטוחות",
};

// Bond Seniority Type
const BOND_SENIORITY_TYPE = {
    unsubordinated_debt: "חוב רגיל",
    senior_debt: "חוב בכיר",
    subordinated_debt: "חוב נחות",
    tier2: "רובד 2",
    secondary_complex: "משני מורכב",
    secondary_subordinated: "משני נחות",
    third_complex: "שלישוני מורכב",
};

// Location in capital structure type
const LOCATION_IN_CAPITAL_STRUCTURE = {
    regular_commitment: "התחייבות רגילה",
    secondary_complex_and_tier2: "הון משני מורכב והון רובד 2",
    secondary_subordinated_and_third_complex: "הון משני נחות ושלישוני מורכב",
};

// Maturity type
const MATURITY_TYPE = {
    bullet: "בלון",
    partial: "לשיעורין",
};

// Interest type
const INTEREST_TYPE = {
    fixed: "קבועה",
    frn: "משתנה",
    zero: "ללא קופון",
};

// Linkage type
const LINKAGE_TYPE = {
    cpi: "מדד",
    non_linked: "שקלי",
    usd: 'דולר ארה"ב',
    eur: "אירו",
    gbp: "לירה שטרלינג",
};

// SP IL Outlook
const SP_IL_OUTLOOK = {
    stable: "יציב",
    positive: "חיובי",
    negative: "שלילי",
};

// Moodys IL Outlook
const MOODYS_IL_OUTLOOK = {
    stable: "יציב",
    positive: "חיובי",
    negative: "שלילי",
};

// Bond coupon frequency (payments per year)
const COUPON_FREQUENCIES = [0, 1, 2, 3, 4, 6, 12];

/** Converts an enum object (e.g. `ASSET_TYPE`) to select field options */
function enumOptions(enumObj) {
    return Object.keys(enumObj).map((k) => {
        return { id: k, label: enumObj[k] };
    });
}

const byLabel = (a, b) => {
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
};

// Determines which additional fields are relevant for the security (see `isIrrelevantField`)
const UNDERLYING_ASSET_EDIT_FIELD = {
    name: "underlying_asset",
    label: "Underlying Asset",
    type: SelectField,
    options: enumOptions(UNDERLYING_ASSET),
};

// Main fields that the user is allowed to edit per security
const MAIN_EDIT_FIELDS = [
    {
        name: "name",
        label: "Name",
        type: TextField,
        required: true,
    },
    {
        name: "ric",
        label: "RIC",
        type: TextField,
        required: true,
    },
    {
        name: "isin",
        label: "ISIN",
        type: TextField,
        required: true,
    },
    {
        name: "symbol",
        label: "Symbol",
        type: TextField,
        required: true,
    },
    {
        name: "infront_symbol",
        label: "Infront Symbol",
        type: TextField,
    },
    {
        name: "exchange_id",
        label: "Exchange",
        type: ExchangeField,
        required: true,
    },
    {
        name: "source_id",
        label: "Source",
        type: SourceField,
        required: true,
    },
    {
        name: "trading_units",
        label: "Trading Units",
        type: DecimalField,
        required: true,
    },
    {
        name: "currency",
        label: "Currency",
        type: CurrencyField,
        required: true,
    },
    {
        name: "settle_days",
        label: "Settle Days",
        type: IntField,
        // So the value can be cleared / left empty (nullable field)
        allowEmpty: true,
    },
    {
        name: "disabled_from",
        label: "Disabled From",
        type: DateField,
        // So we can allow the user to clear the date (in case we want to re-enable the security)
        allowEmpty: true,
    },
];

// Additional fields that the user is allowed to edit per security - `underlyingAssets` (if set) lists the underlying
// assets that the field is relevant for, otherwise it's relevant for all securities
const MORE_EDIT_FIELDS = [
    // Identifiers
    { name: "name_hebrew", label: "Name Hebrew", type: TextField },
    { name: "equity_symbol", label: "Equity Symbol", type: TextField },
    { name: "cusip", label: "CUSIP", type: TextField },
    { name: "sedol", label: "SEDOL", type: TextField },
    {
        name: "security_exchange_id",
        label: "Security Exchange Id",
        type: TextField,
    },
    {
        name: "security_exchange_ticker",
        label: "Security Exchange Ticker",
        type: TextField,
    },
    {
        name: "underlying_security_id",
        label: "Underlying Security",
        type: SecurityField,
        underlyingAssets: ["bonds"],
    },

    // Trading
    {
        name: "minimum_trading_units",
        label: "Minimum Trading Units",
        type: IntField,
    },
    {
        name: "trading_status",
        label: "Trading Status",
        type: SelectField,
        options: enumOptions(TRADING_STATUS),
    },
    { name: "ipo_date", label: "IPO Date", type: DateField },
    { name: "listing_date", label: "Listing Date", type: DateField },
    { name: "delisting_date", label: "Delisting Date", type: DateField },
    {
        name: "tase_dual_listing",
        label: "TASE Dual Listing",
        type: BooleanField,
        underlyingAssets: ["equity"],
    },

    // Issuer
    { name: "issuer_id", label: "Issuer ID", type: TextField },
    { name: "issuer_name", label: "Issuer Name", type: TextField },
    {
        name: "issuer_name_hebrew",
        label: "Issuer Name Hebrew",
        type: TextField,
    },
    { name: "issuer_parent_id", label: "Issuer Parent ID", type: TextField },
    {
        name: "controlling_shareholder_hebrew",
        label: "Controlling Shareholder Hebrew",
        type: TextField,
    },

    // Classification
    {
        name: "asset_type",
        label: "Asset Type",
        type: SelectField,
        options: enumOptions(ASSET_TYPE),
    },
    {
        name: "issue_type",
        label: "Issue Type",
        type: SelectField,
        options: enumOptions(ISSUE_TYPE),
    },
    {
        name: "sector_class",
        label: "Sector Class",
        type: SelectField,
        options: enumOptions(SECTOR_CLASS),
    },
    {
        name: "tase_industry",
        label: "TASE Industry",
        type: SelectField,
        options: enumOptions(TASE_INDUSTRY),
    },
    {
        name: "tase_sub_industry",
        label: "TASE Sub Industry",
        type: SelectField,
        options: enumOptions(TASE_SUB_INDUSTRY),
    },

    // Country
    {
        name: "domicile_country_code",
        label: "Domicile Country",
        type: CountryField,
    },
    {
        name: "country_of_incorporation",
        label: "Country of Incorporation",
        type: CountryField,
    },
    { name: "country_hq", label: "Country HQ", type: CountryField },
    { name: "country_class", label: "Country Class", type: CountryField },
    {
        name: "israeli_linkage",
        label: "Israeli Linkage",
        type: BooleanField,
    },

    // Bond terms
    {
        name: "bond_maturity_date",
        label: "Bond Maturity Date",
        type: DateField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "bond_early_maturity_date",
        label: "Bond Early Maturity Date",
        type: DateField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "bond_maturity_year",
        label: "Bond Maturity Year",
        type: DateField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "annual_coupon",
        label: "Annual Coupon",
        type: DecimalField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "coupon_frequency",
        label: "Coupon Frequency",
        type: SelectField,
        options: COUPON_FREQUENCIES.map((x) => ({ id: x, label: `${x}` })),
        underlyingAssets: ["bonds"],
    },
    {
        name: "maturity_type",
        label: "Maturity Type",
        type: SelectField,
        options: enumOptions(MATURITY_TYPE),
        underlyingAssets: ["bonds"],
    },
    {
        name: "interest_type",
        label: "Interest Type",
        type: SelectField,
        options: enumOptions(INTEREST_TYPE),
        underlyingAssets: ["bonds"],
    },
    {
        name: "linkage_type",
        label: "Linkage Type",
        type: SelectField,
        options: enumOptions(LINKAGE_TYPE),
        underlyingAssets: ["bonds"],
    },
    {
        name: "negative_linkage_cap",
        label: "Negative Linkage Cap",
        type: BooleanField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "convertible_deletion_option",
        label: "Convertible Deletion Option",
        type: BooleanField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "credit_default_date",
        label: "Credit Default Date",
        type: DateField,
        underlyingAssets: ["bonds"],
    },

    // Collateral and seniority
    {
        name: "collateral_type",
        label: "Collateral Type",
        type: SelectField,
        options: enumOptions(COLLATERAL_TYPE),
        underlyingAssets: ["bonds"],
    },
    {
        name: "collateral_description",
        label: "Collateral Description",
        type: SelectField,
        options: enumOptions(COLLATERAL_DESCRIPTION),
        underlyingAssets: ["bonds"],
    },
    {
        name: "bond_seniority_type",
        label: "Bond Seniority Type",
        type: SelectField,
        options: enumOptions(BOND_SENIORITY_TYPE),
        underlyingAssets: ["bonds"],
    },
    {
        name: "location_in_capital_structure",
        label: "Location In Capital Structure",
        type: SelectField,
        options: enumOptions(LOCATION_IN_CAPITAL_STRUCTURE),
        underlyingAssets: ["bonds"],
    },

    // Credit ratings
    {
        name: "sp_il_rating",
        label: "SP IL Rating",
        type: TextField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "sp_il_outlook",
        label: "SP IL Outlook",
        type: SelectField,
        options: enumOptions(SP_IL_OUTLOOK),
        underlyingAssets: ["bonds"],
    },
    {
        name: "sp_il_rating_last_change",
        label: "SP IL Rating Last Change",
        type: DateField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "moodys_il_rating",
        label: "Moodys IL Rating",
        type: TextField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "moodys_il_outlook",
        label: "Moodys IL Outlook",
        type: SelectField,
        options: enumOptions(MOODYS_IL_OUTLOOK),
        underlyingAssets: ["bonds"],
    },
    {
        name: "moodys_il_rating_last_change",
        label: "Moodys IL Rating Last Change",
        type: DateField,
        underlyingAssets: ["bonds"],
    },
    {
        name: "credit_rating_tranche",
        label: "Credit Rating Tranche",
        type: TextField,
        underlyingAssets: ["bonds"],
    },
];

/** Is the field irrelevant for the security's (currently selected) underlying asset */
function isIrrelevantField(field, values) {
    // Select field values are raw IDs until the input converts them to {id, label} options
    const underlyingAsset =
        values.underlying_asset?.id ?? values.underlying_asset;

    // Fields are only filtered for underlying assets that have a known set of relevant fields (bonds / equity)
    return (
        field.underlyingAssets !== undefined &&
        ["bonds", "equity"].includes(underlyingAsset) &&
        !field.underlyingAssets.includes(underlyingAsset)
    );
}

// All fields of the edit dialog - underlying asset first (as it determines the relevant fields), then the main fields,
// followed by the (collapsed) additional fields
const EDIT_FIELDS = [
    UNDERLYING_ASSET_EDIT_FIELD,
    ...[...MAIN_EDIT_FIELDS].sort(byLabel),
    ...[...MORE_EDIT_FIELDS].sort(byLabel).map((f) => ({
        ...f,
        collapsed: true,
        hidden: (values) => isIrrelevantField(f, values),
    })),
];

const EDIT_FIELDS_BY_NAME = Object.fromEntries(
    EDIT_FIELDS.map((f) => [f.name, f]),
);

/** Normalizes a field value (either from the record or the submitted form), so they can be compared */
function normalizeForCompare(field, value) {
    if (value === undefined || value === null || value === "") return null;
    // Record dates are Unix epoch timestamps, while submitted dates are already formatted
    if (field?.type === DateField && typeof value === "number") {
        return DateTime.fromMillis(value).toFormat(API_DATE_FORMAT);
    }
    return String(value);
}

/** Returns only the submitted values that differ from the record's values (the API expects only changed fields) */
function changedValues(values, record) {
    return Object.fromEntries(
        Object.entries(values).filter(
            ([name, value]) =>
                normalizeForCompare(EDIT_FIELDS_BY_NAME[name], value) !==
                normalizeForCompare(EDIT_FIELDS_BY_NAME[name], record[name]),
        ),
    );
}

/** Returns the values without the new data start date (only used when saving new data) */
function withoutStartDate({ start_date, ...values }) {
    return values;
}

export default function ManagedSecurities() {
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [editRowIndex, setEditRowIndex] = useState(0);
    const [managedSecurities, setManagedSecurities] = useState([]);
    const exchangesById = useSelector((state) => state.exchanges.exchangesById);
    const sourcesById = useSelector((state) => state.securities.sourcesById);
    const queryDataTable = useRef();
    const securitiesById = useSelector(
        (state) => state.securities.securitiesById,
    );

    const makeEnumField = (key, label, enumObj) => ({
        name: key,
        label: label,
        type: MultipleSelectField,
        options: enumOptions(enumObj),
    });

    const TABLE_OPTIONS = {
        sortOrder: {
            name: "name",
            direction: "asc",
        },
        customIcons: [
            {
                title: "Add New Security",
                icon: <Add />,
                onClick: () => {
                    setShowAddDialog(true);
                },
            },
        ],
        onEditRow: (index) => {
            setEditRowIndex(index);
            setShowEditDialog(true);
        },
    };

    const COLUMNS = [
        freeTextColumn("name", "Name"),
        freeTextColumn("ric", "RIC"),
        freeTextColumn("isin", "ISIN"),
        freeTextColumn("symbol", "Symbol"),
        freeTextColumn("infront_symbol", "Infront Symbol"),
        multiSelectColumn("trading_units", "Trading Units"),
        nonFilterableColumn("settle_days", "Settle Days"),
        multiSelectColumn(
            "domicile_country_code",
            "Domicile Country",
            customValueRenderer(getCountryName),
        ),
        multiSelectColumn(
            "exchange_id",
            "Exchange",
            customValueRenderer((x) => exchangesById[x].name),
        ),
        dateFilterColumn("disabled_from", "Disabled From"),
        multiSelectColumn(
            "source_id",
            "Source",
            customValueRenderer((x) => sourcesById[x].name),
        ),
        multiSelectColumn("issuer_id", "Issuer ID", { display: false }),
        multiSelectColumn("issuer_name", "Issuer Name", { display: false }),
        multiSelectColumn("issuer_name_hebrew", "Issuer Name Hebrew", {
            display: false,
        }),
        multiSelectColumn("issuer_parent_id", "Issuer Parent ID", {
            display: false,
        }),
        multiSelectColumn(
            "underlying_asset",
            "Underlying Asset",
            customValueRenderer((x) => UNDERLYING_ASSET[x]),
        ),

        // Optionally displayed columns
        multiSelectColumn("currency", "Currency", { display: false }),
        freeTextColumn("security_exchange_id", "Security Exchange Id", {
            display: false,
        }),
        freeTextColumn("security_exchange_ticker", "Security Exchange Ticker", {
            display: false,
        }),
        freeTextColumn("cusip", "CUSIP", { display: false }),
        freeTextColumn("sedol", "SEDOL", { display: false }),
        freeTextColumn("name_hebrew", "Name Hebrew", { display: false }),
        multiSelectColumn("asset_type", "Asset Type", {
            ...customValueRenderer((x) => ASSET_TYPE[x]),
            display: false,
        }),
        multiSelectColumn("issue_type", "Issue Type", {
            ...customValueRenderer((x) => ISSUE_TYPE[x]),
            display: false,
        }),
        freeTextColumn(
            "controlling_shareholder_hebrew",
            "Controlling Shareholder Hebrew",
            { display: false },
        ),
        dropDownColumn("israeli_linkage", "Israeli Linkage", {
            ...booleanValueRenderer(),
            display: false,
        }),
        multiSelectColumn(
            "country_of_incorporation",
            "Country of Incorporation",
            { ...customValueRenderer(getCountryName), display: false },
        ),
        multiSelectColumn("country_hq", "Country HQ", {
            ...customValueRenderer(getCountryName),
            display: false,
        }),
        multiSelectColumn("country_class", "Country Class", {
            ...customValueRenderer(getCountryName),
            display: false,
        }),
        multiSelectColumn("tase_sub_industry", "TASE Sub Industry", {
            ...customValueRenderer((x) => TASE_SUB_INDUSTRY[x]),
            display: false,
        }),
        multiSelectColumn("tase_industry", "TASE Industry", {
            ...customValueRenderer((x) => TASE_INDUSTRY[x]),
            display: false,
        }),
        multiSelectColumn("sector_class", "Sector Class", {
            ...customValueRenderer((x) => SECTOR_CLASS[x]),
            display: false,
        }),
        dropDownColumn("tase_dual_listing", "TASE Dual Listing", {
            ...booleanValueRenderer(),
            display: false,
        }),
        multiSelectColumn("trading_status", "Trading Status", {
            ...customValueRenderer((x) => TRADING_STATUS[x]),
            display: false,
        }),
        dateFilterColumn("ipo_date", "IPO Date", { display: false }),
        dateFilterColumn("listing_date", "Listing Date", { display: false }),
        dateFilterColumn("delisting_date", "Delisting Date", {
            display: false,
        }),
        dateFilterColumn("bond_maturity_date", "Bond Maturity Date", {
            display: false,
        }),
        dateFilterColumn(
            "bond_early_maturity_date",
            "Bond Early Maturity Date",
            { display: false },
        ),
        multiSelectColumn("bond_maturity_year", "Bond Maturity Year", {
            ...dateValueRenderer("yyyy"),
            display: false,
        }),
        nonFilterableColumn("annual_coupon", "Annual Coupon", {
            display: false,
        }),
        multiSelectColumn("coupon_frequency", "Coupon Frequency", {
            display: false,
        }),
        nonFilterableColumn("minimum_trading_units", "Minimum Trading Units", {
            display: false,
        }),
        dateFilterColumn("credit_default_date", "Credit Default Date", {
            display: false,
        }),
        dropDownColumn(
            "convertible_deletion_option",
            "Convertible Deletion Option",
            { ...booleanValueRenderer(), display: false },
        ),
        multiSelectColumn("collateral_type", "Collateral Type", {
            ...customValueRenderer((x) => COLLATERAL_TYPE[x]),
            display: false,
        }),
        multiSelectColumn("collateral_description", "Collateral Description", {
            ...customValueRenderer((x) => COLLATERAL_DESCRIPTION[x]),
            display: false,
        }),
        freeTextColumn("underlying_security_id", "Underlying Security", {
            ...customValueRenderer((x) =>
                x ? getSecurityName(securitiesById[x]) : "",
            ),
            display: false,
        }),
        multiSelectColumn("bond_seniority_type", "Bond Seniority Type", {
            ...customValueRenderer((x) => BOND_SENIORITY_TYPE[x]),
            display: false,
        }),
        multiSelectColumn(
            "location_in_capital_structure",
            "Location In Capital Structure",
            {
                ...customValueRenderer((x) => LOCATION_IN_CAPITAL_STRUCTURE[x]),
                display: false,
            },
        ),
        multiSelectColumn("maturity_type", "Maturity Type", {
            ...customValueRenderer((x) => MATURITY_TYPE[x]),
            display: false,
        }),
        multiSelectColumn("interest_type", "Interest Type", {
            ...customValueRenderer((x) => INTEREST_TYPE[x]),
            display: false,
        }),
        multiSelectColumn("linkage_type", "Linkage Type", {
            ...customValueRenderer((x) => LINKAGE_TYPE[x]),
            display: false,
        }),
        dropDownColumn("negative_linkage_cap", "Negative Linkage Cap", {
            ...booleanValueRenderer(),
            display: false,
        }),
        multiSelectColumn("sp_il_rating", "SP IL Rating", {
            display: false,
        }),
        multiSelectColumn("moodys_il_rating", "Moodys IL Rating", {
            display: false,
        }),
        multiSelectColumn("credit_rating_tranche", "Credit Rating Tranche", {
            display: false,
        }),
        dateFilterColumn(
            "sp_il_rating_last_change",
            "SP IL Rating Last Change",
            { display: false },
        ),
        dateFilterColumn(
            "moodys_il_rating_last_change",
            "Moodys IL Rating Last Change",
            { display: false },
        ),
        multiSelectColumn("sp_il_outlook", "SP IL Outlook", {
            ...customValueRenderer((x) => SP_IL_OUTLOOK[x]),
            display: false,
        }),
        multiSelectColumn("moodys_il_outlook", "Moodys IL Outlook", {
            ...customValueRenderer((x) => MOODYS_IL_OUTLOOK[x]),
            display: false,
        }),
        freeTextColumn("equity_symbol", "Equity Symbol", { display: false }),
    ];

    const QUERY_FIELDS = [
        {
            name: "ric",
            label: "RIC",
            type: TextField,
        },
        {
            name: "isin",
            label: "ISIN",
            type: TextField,
        },
        {
            name: "symbol",
            label: "Symbol",
            type: TextField,
        },
        {
            name: "security_exchange_id",
            label: "Security Exchange ID",
            type: TextField,
        },
        {
            name: "name",
            label: "Name",
            type: TextField,
        },
        {
            name: "name_hebrew",
            label: "Name Hebrew",
            type: TextField,
        },
        {
            name: "issuer_name",
            label: "Issuer Name",
            type: TextField,
        },
        {
            name: "issuer_name_hebrew",
            label: "Issuer Name Hebrew",
            type: TextField,
        },
        {
            name: "domicile_country_codes",
            label: "Domicile Country",
            type: MultipleCountriesField,
        },
        {
            name: "exchanges",
            label: "Exchange",
            type: MultipleExchangesField,
        },

        {
            name: "sources",
            label: "Source",
            type: MultipleSourcesField,
        },
        {
            name: "disabled_from",
            label: "Disabled From",
            type: DateField,
        },
        {
            name: "bond_early_maturity_date",
            label: "Bond Early Maturity Date",
            type: DateField,
        },
        makeEnumField("sector_class", "Sector Class", SECTOR_CLASS),
        makeEnumField("underlying_asset", "Underlying Asset", UNDERLYING_ASSET),
        makeEnumField("tase_industry", "TASE Industry", TASE_INDUSTRY),
        makeEnumField(
            "tase_sub_industry",
            "TASE Sub Industry",
            TASE_SUB_INDUSTRY,
        ),
    ];

    // When the new data (see "save security changes") takes effect - only used when saving new data
    const START_DATE_EDIT_FIELD = {
        name: "start_date",
        label: "New Data Start Date",
        type: DateField,
        defaultValue: DateTime.now().startOf("day").toMillis(),
        collapsed: true,
    };

    /** Converts the submitted form values to the format the API expects */
    const toApiValues = (values) => {
        const apiValues = { ...values };

        // The API identifies the underlying security by its security exchange ID (not internal ID)
        if (apiValues.underlying_security_id) {
            const underlyingSecurity =
                securitiesById[apiValues.underlying_security_id];
            if (!underlyingSecurity?.security_exchange_id) {
                throw new Error(
                    "The underlying security doesn't have a Security Exchange Id",
                );
            }
            apiValues.underlying_security_id =
                underlyingSecurity.security_exchange_id;
        }

        // Only the year is saved
        if (apiValues.bond_maturity_year) {
            apiValues.bond_maturity_year = apiValues.bond_maturity_year.slice(
                0,
                4,
            );
        }

        return apiValues;
    };

    /** Returns the changed values of the edited security, in the API format - fails if nothing changed */
    const getEditedValues = (values, record) => {
        const changes = changedValues(withoutStartDate(values), record);
        if (Object.keys(changes).length === 0) {
            throw new Error("No changes to save");
        }
        return toApiValues(changes);
    };

    /** Creates a new security (new internal ID) from the submitted values */
    const createSecurity = async (values) =>
        gApiClient.callApi("security", {
            method: "POST",
            body: toApiValues(withoutStartDate(values)),
        });

    const editedRecord = managedSecurities[editRowIndex];

    const EDIT_SUBMIT_ACTIONS = [
        {
            // Keeps the historical data - creates a new row for the security from the start date (closing the
            // current row the day before)
            text: "save security changes",
            icon: <Save />,
            onSubmit: async (values, extraBody) =>
                gApiClient.callApi(`security/${editedRecord.internal_id}`, {
                    method: "PUT",
                    body: {
                        ...getEditedValues(values, editedRecord),
                        start_date: values.start_date,
                        // e.g. trash_future_actions, when resent after the future actions confirmation
                        ...extraBody,
                    },
                }),
        },
        {
            // Overrides the security's current row (no history kept) - mostly used for fixing errors
            text: "Overwrite Fields",
            color: "warning",
            icon: <Edit />,
            confirmMessage:
                "This overwrites the security's current data in place, with no history. Continue?",
            onSubmit: async (values) => {
                const response = await gApiClient.callApi(
                    `security_row/${editedRecord._id}`,
                    {
                        method: "PUT",
                        body: getEditedValues(values, editedRecord),
                    },
                );
                // Database errors are returned as a successful response with an error message
                if (response.data?.error) throw new Error(response.data.error);
                return response;
            },
        },
        {
            // Creates a new security (new internal ID) from the edited values
            text: "Save as New Security",
            color: "success",
            icon: <LibraryAdd />,
            onSubmit: createSecurity,
        },
    ];

    const onFinishedEditingRow = async (updatedRecord) => {
        // Refresh results to show latest updated record
        queryDataTable.current.refreshResults();
        setShowEditDialog(false);
    };

    const onFinishedAddingRow = async () => {
        // Refresh results to show the added security
        queryDataTable.current.refreshResults();
        setShowAddDialog(false);
    };

    const onFinishedImporting = async () => {
        // Refresh results to show imported securities
        queryDataTable.current.refreshResults();
        setShowImportDialog(false);
    };

    return (
        <>
            <AddRecordDialog
                title="Add Managed Security"
                show={showAddDialog}
                fields={EDIT_FIELDS}
                submitActions={[
                    { text: "Add", icon: <Add />, onSubmit: createSecurity },
                ]}
                onCancelled={() => setShowAddDialog(false)}
                onFinished={onFinishedAddingRow}
            />
            <EditRecordDialog
                title="Edit Managed Security"
                variant="wide"
                show={showEditDialog}
                fields={[...EDIT_FIELDS, START_DATE_EDIT_FIELD]}
                record={editedRecord}
                submitActions={EDIT_SUBMIT_ACTIONS}
                onCancelled={() => setShowEditDialog(false)}
                onFinished={onFinishedEditingRow}
            />
            <FileImporterDialog
                title="Bulk Import Managed Securities"
                importsText="Securities Imported"
                show={showImportDialog}
                uploadAPIPath={"import_managed_securities"}
                changesKey="securities"
                importsTableColumns={COLUMNS}
                onCancelled={() => setShowImportDialog(false)}
                onFinished={onFinishedImporting}
            />
            <QueryDataTable
                actions={
                    <Tooltip title={"Bulk Import Securities"}>
                        <IconButton onClick={() => setShowImportDialog(true)}>
                            <FileUpload />
                        </IconButton>
                    </Tooltip>
                }
                ref={queryDataTable}
                tableColumns={COLUMNS}
                tableOptions={TABLE_OPTIONS}
                queryFields={QUERY_FIELDS}
                queryAPIPath="managed_securities"
                onResults={setManagedSecurities}
                dynamicLoad={true}
            />
        </>
    );
}
